/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react';
import { SectionHeading } from '#components/headings.jsx';
import Select from '#lib/components/select.jsx';
import Checkbox from '#lib/components/checkbox.jsx';
import { defaultAccessValue, defaultDisplayValue, defaultMakeReactKey } from '#helpers/helpers.js';
import { addClasses, cn } from '#lib/helpers/class.js';
import { Icon } from '@iconify-icon/react';
import Button from '#lib/components/button.jsx';

export function Prices(props) {
    const {
        form,
        currency,
        percentageType,
        percentageTypes,
        symbol,
        enablePlanPrice,
        totalPrice,
        currencies,
        plans,
        onEditPlan
    } = props;

    const [exchangeRate, setExchangeRate] = useState(
        currencies?.find((c) => c.name === currency || c.id === currency)?.exchangeRate
    );

    // update total price when currency changes
    useEffect(() => {
        const currentExchangeRate = currencies?.find((c) => c.name === currency || c.id === currency)?.exchangeRate;
        if (!currentExchangeRate || !exchangeRate || currentExchangeRate === exchangeRate) {
            setExchangeRate(currentExchangeRate);
            return;
        }
        const newPrice = (totalPrice * currentExchangeRate) / exchangeRate;
        form.handleChange('totalPrice', newPrice.toFixed(2));
        plans.forEach((plan) => {
            if (!plan.price) return;
            const newPlanPrice = (plan.price * currentExchangeRate) / exchangeRate;
            onEditPlan([`day-${plan.dayIndex}`, plan.id], 'price', newPlanPrice.toFixed(2));
        });
        setExchangeRate(currentExchangeRate);
    }, [currency]);

    // calculate total price
    const calculateItineraryPrice = useCallback(() => {
        const newPrice = plans.reduce((acc, plan) => {
            if (!plan.price || typeof Number(plan.price) !== 'number') return acc;
            return acc + Number(plan.price);
        }, 0);
        form.handleChange('totalPrice', newPrice.toFixed(2));
    }, [plans]);

    return (
        <form className="contents" {...form.props.form}>
            <section className="flex flex-col space-y-2 col-span-full">
                <SectionHeading id="prices" subtext={_t.edit.currencyAndPricesDescription()}>
                    {_t.edit.currencyAndPrices()}
                </SectionHeading>
                <div className="flex flex-col gap-2">
                    <div className="flex w-full gap-2">
                        <Checkbox.Full
                            className="flex gap-2 w-fit"
                            label={_t.edit.fields.enableTotalPrice()}
                            {...form.registerCheckbox('enableTotalPrice')}
                        />
                        <Checkbox.Full
                            className="flex gap-2 w-fit"
                            label={_t.edit.fields.enablePlanPrice()}
                            {...form.registerCheckbox('enablePlanPrice')}
                        />
                    </div>
                    <span
                        className={cn(
                            'italic text-sm text-neutral-700 transition-all duration-500',
                            enablePlanPrice ? 'h-auto opacity-100' : 'h-0 overflow-hidden opacity-0'
                        )}
                    >
                        {_t.edit.planPriceEnabledText()}
                    </span>
                    <div className="flex gap-2 lg:justify-start">
                        <div className="flex flex-col gap-2">
                            <p className="w-1/2 text-sm">{_t.edit.currency()}</p>
                            <div className="w-[160px]">
                                <Select.Full
                                    name="currency"
                                    options={currencies}
                                    placeholder={_t.edit.selectProfile()}
                                    value={currency}
                                    makeReactKey={defaultMakeReactKey}
                                    accessValue={defaultAccessValue}
                                    displayValue={defaultDisplayValue}
                                    {...form.register('currency')}
                                />
                            </div>
                        </div>
                        <div className="flex flex-col w-full gap-2">
                            <span className="w-1/2 text-sm">{_t.edit.totalPrice()}</span>
                            <div className="flex items-center gap-2">
                                <div className="relative w-[160px] flex gap-2 items-center">
                                    <span className="absolute left-[10px] top-[6px] z-10 font-semibold">{symbol}</span>
                                    <input
                                        name="totalPrice"
                                        type="number"
                                        min="0"
                                        step="any"
                                        pattern="\d+(\.\d{2})?"
                                        placeholder={_t.edit.totalPrice()}
                                        className="w-full p-2 pl-6 input-box input-inside-elements"
                                        onBlur={() => form.handleChange('totalPrice', Number(totalPrice).toFixed(2))}
                                        {...form.register('totalPrice')}
                                    />
                                </div>
                                <Button
                                    look="white"
                                    size="sm"
                                    className={addClasses('flex justify-center items-center')}
                                    style={{ paddingTop: '.6rem', paddingBottom: '.6rem' }}
                                    onClick={calculateItineraryPrice}
                                >
                                    <Icon className="px-2" width={16} height={16} icon="iconoir:calculator" />
                                    <span>{_t.edit.calculate()}</span>
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2 lg:justify-start">
                        <div className="flex flex-col w-full gap-2">
                            <span className="w-1/2 text-sm">{_t.edit.fields.percentage()}</span>
                            <div className="flex items-stretch gap-2">
                                <div className="relative w-[160px] flex gap-2 items-center">
                                    <span className="absolute left-[10px] z-10 font-semibold">%</span>
                                    <input
                                        name="percentage"
                                        type="number"
                                        min="-100"
                                        max="100"
                                        step="1"
                                        pattern="\d+(\.\d{2})?"
                                        placeholder={_t.edit.fields.percentage()}
                                        className="w-full p-2 pl-8 input-box input-inside-elements"
                                        {...form.register('percentage')}
                                    />
                                </div>
                                <Button
                                    look="white"
                                    size="sm"
                                    className={addClasses('flex justify-center items-center')}
                                    style={{ paddingTop: '.6rem', paddingBottom: '.6rem' }}
                                    onClick={() => form.handleChange('percentage', 0)}
                                >
                                    <span>{_t.edit.resetPercentage()}</span>
                                </Button>
                                <div className="w-[180px]">
                                    <Select.Full
                                        name="percentageType"
                                        options={percentageTypes}
                                        placeholder={_t.edit.selectProfile()}
                                        value={percentageType ?? "MARKUP"}
                                        makeReactKey={defaultMakeReactKey}
                                        accessValue={defaultAccessValue}
                                        displayValue={(option) => percentageTypes.find((el) => el.id == option).name}
                                        {...form.register('percentageType')}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </form>
    );
}
